// タイムゾーン設定のコンボボックス。
// サーバが描画した<select>を隠し、テキスト入力で選択肢を絞り込んで選べるUIに置き換える。
// フォームで送信される値は隠した<select>が持ち続けるので、JSが無効でも通常のセレクトとして動く。

type Choice = {
  value: string;
  label: string;
  // 絞り込み比較用に小文字化し、"_"を空白に置き換えた文字列（"new york"でAmerica/New_Yorkに当てるため）
  searchText: string;
};

const normalize = (text: string) => text.toLowerCase().replace(/_/g, ' ');

const matches = (choice: Choice, query: string) => {
  const tokens = normalize(query).split(/\s+/).filter((token) => token !== '');
  return tokens.every((token) => choice.searchText.includes(token));
};

export function setTimeZonePicker() {
  const select = document.getElementById('timeZoneSelect');
  if (!select || !(select instanceof HTMLSelectElement) || select.dataset.pickerReady === 'true') {
    return;
  }
  select.dataset.pickerReady = 'true';

  const choices: Choice[] = Array.from(select.options).map((option) => ({
    value: option.value,
    label: option.text,
    searchText: normalize(option.text),
  }));
  const labelOf = (value: string) => choices.find((choice) => choice.value === value)?.label ?? '';

  const wrapper = document.createElement('div');
  wrapper.className = 'time-zone-picker';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'form-control';
  input.autocomplete = 'off';
  input.placeholder = select.dataset.searchPlaceholder ?? '';
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  input.value = labelOf(select.value);

  const list = document.createElement('ul');
  list.className = 'time-zone-picker-list d-none';
  list.setAttribute('role', 'listbox');
  list.id = 'timeZonePickerList';
  input.setAttribute('aria-controls', list.id);

  const empty = document.createElement('li');
  empty.className = 'time-zone-picker-empty';
  empty.textContent = select.dataset.noMatch ?? '';

  select.classList.add('d-none');
  select.insertAdjacentElement('afterend', wrapper);
  wrapper.append(input, list);

  let visible: Choice[] = [];
  let activeIndex = -1;

  const close = () => {
    list.classList.add('d-none');
    input.setAttribute('aria-expanded', 'false');
    activeIndex = -1;
  };

  // 入力欄の内容が選択中の値と一致しないまま閉じたら、選択中のラベルに戻す
  const restore = () => {
    input.value = labelOf(select.value);
  };

  const choose = (choice: Choice) => {
    select.value = choice.value;
    input.value = choice.label;
    close();
  };

  const highlight = (index: number) => {
    const items = Array.from(list.querySelectorAll<HTMLLIElement>('[role=option]'));
    items.forEach((item, i) => {
      const active = i === index;
      item.classList.toggle('active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    activeIndex = index;
    if (index >= 0) {
      items[index].scrollIntoView({ block: 'nearest' });
    }
  };

  const render = (query: string) => {
    visible = choices.filter((choice) => matches(choice, query));
    list.replaceChildren();

    if (visible.length === 0) {
      list.append(empty);
    } else {
      visible.forEach((choice, index) => {
        const item = document.createElement('li');
        item.setAttribute('role', 'option');
        item.textContent = choice.label;
        // blurより先に発火するmousedownで選ぶ（clickだとblurでリストが閉じてしまう）
        item.addEventListener('mousedown', (event) => {
          event.preventDefault();
          choose(choice);
        });
        list.append(item);
      });
    }

    list.classList.remove('d-none');
    input.setAttribute('aria-expanded', 'true');
    highlight(visible.findIndex((choice) => choice.value === select.value));
  };

  // フォーカス時は全件を出し、選択中の項目までスクロールする
  input.addEventListener('focus', () => {
    input.select();
    render('');
  });

  input.addEventListener('input', () => {
    render(input.value);
  });

  input.addEventListener('blur', () => {
    close();
    restore();
  });

  input.addEventListener('keydown', (event) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (list.classList.contains('d-none')) {
          render(input.value);
        } else if (visible.length > 0) {
          highlight((activeIndex + 1) % visible.length);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (visible.length > 0) {
          highlight((activeIndex - 1 + visible.length) % visible.length);
        }
        break;
      case 'Enter':
        // 選択肢が開いているときのEnterはフォーム送信ではなく選択にする
        if (!list.classList.contains('d-none')) {
          event.preventDefault();
          if (activeIndex >= 0) {
            choose(visible[activeIndex]);
          } else if (visible.length === 1) {
            choose(visible[0]);
          }
        }
        break;
      case 'Escape':
        if (!list.classList.contains('d-none')) {
          event.preventDefault();
          close();
          restore();
        }
        break;
      default:
        break;
    }
  });
}

export default setTimeZonePicker;
